// Zurück-Geste schließt ein Vollbild (ST-UX-001, NFR-UX-004).
//
// Am Handy sieht ein Vollbild (Befehlspalette, Ganze Notiz) aus wie eine eigene
// Seite; die Zurück-Geste muss es schließen und darf die Seite darunter nicht
// verlassen. Dafür bekommt das Vollbild einen eigenen Verlaufseintrag mit dem
// Stand des Eintrags darunter (Scrollstand aus rahmen-scroll.ts), damit die
// Rückkehr dieselbe Stelle findet. Schließt man selbst (Esc, X, Auswahl), nimmt
// der Hook den Eintrag wieder zurück; sonst führte das nächste Zurück in ein
// leeres Vollbild.

import { useEffect, useRef } from 'react';

/** Bis hierhin sind Palette und Dialoge ein Vollbild (wie in layout.tsx und den Stilen). */
export const VOLLBILD_ABFRAGE = '(max-width: 640px)';

export interface ZurueckGeste {
  /** Beim Öffnen: legt den Eintrag an, wenn `abfrage` gerade zutrifft. */
  readonly eintragen: () => void;
  /**
   * Beim Schließen ohne Geste: nimmt den Eintrag zurück. `danach` läuft erst,
   * wenn der Browser wieder auf dem Eintrag darunter steht -- eine Navigation
   * folgt so auf die Seite statt auf das Vollbild.
   */
  readonly zuruecknehmen: (danach?: () => void) => void;
}

/** `beiGeste` schließt das Vollbild, wenn der Nutzer selbst zurückgeht. */
export function useZurueckSchliesstVollbild(beiGeste: () => void, abfrage: string = VOLLBILD_ABFRAGE): ZurueckGeste {
  const eintrag = useRef(false);
  const nachRuecknahme = useRef<(() => void) | null>(null);
  const geste = useRef(beiGeste);
  geste.current = beiGeste;

  useEffect(() => {
    const beiZurueck = (): void => {
      const danach = nachRuecknahme.current;
      if (danach !== null) {
        nachRuecknahme.current = null;
        danach();
        return;
      }
      if (!eintrag.current) return;
      eintrag.current = false;
      geste.current();
    };
    window.addEventListener('popstate', beiZurueck);
    return () => window.removeEventListener('popstate', beiZurueck);
  }, []);

  const eintragen = (): void => {
    if (eintrag.current || typeof window.matchMedia !== 'function' || !window.matchMedia(abfrage).matches) return;
    const state: unknown = window.history.state;
    window.history.pushState({ ...(typeof state === 'object' && state !== null ? state : {}), liaisonVollbild: true }, '');
    eintrag.current = true;
  };

  const zuruecknehmen = (danach?: () => void): void => {
    if (!eintrag.current) {
      danach?.();
      return;
    }
    // Ohne Folgehandlung bleibt nichts stehen: das popstate der Rücknahme
    // findet keinen Eintrag mehr und tut nichts.
    eintrag.current = false;
    nachRuecknahme.current = danach ?? null;
    window.history.back();
  };

  return { eintragen, zuruecknehmen };
}
